import React, { useState, useEffect, useRef, useMemo } from "react"
import { DateTime } from "luxon"
import { useSelector, useDispatch } from "react-redux"
import ConfigContainer from "components/ConfigContainer"
import { Container, Title, Select, Text, Checkbox } from "components/Form"
import { Button } from "react-bootstrap"
import { Popup } from "components"
import { FiTrash2, FiSettings } from "react-icons/fi"
import { sampleView } from "state_management"
import useT from "utils/hooks/useT"

// A form component reused for both Add and Edit sample operations.
export default function AddEditSamplePopup({
  initial = {},
  analysisCollection = [],
  productCollection = [],
  onCancel = () => {},
  onSubmit = () => {},
  title = "Edit sample",
  isEdit = false,
}) {
  const { t } = useT("common")
  const teamTimezone = useSelector((state) => state.teamInfo?.timezone || 'UTC')
  const dispatch = useDispatch()

  const [form, setForm] = useState({
    sampleName: initial.sampleName || "",
    // For edit mode analyses should start empty (user can only add new ones)
    analysisName: initial.analysisName || [],
    observationId: initial.observationId || "1",
    numberOfObservations: initial.numberOfObservations || 1,
    arrival: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('yyyy-MM-dd') : DateTime.now().setZone(teamTimezone).startOf('day').toFormat('yyyy-MM-dd'),
    arrivalTime: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('HH:mm') : '00:00',
    deadline: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('yyyy-MM-dd') : "",
    deadlineTime: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('HH:mm') : '23:59',
    priority: initial.priority || 2,
    status: initial.status || 'TODO',
    productName: initial.productName || null,
    exclusive: !!initial.exclusive,
    isFlagged: !!initial.isFlagged,
  })


  // Access product analysis relationships from store to auto-fill analyses when selecting a product
  const productAnalysis = useSelector((state) => state.sampleView?.productAnalysis || {})

  // State for showing analyses popup
  const [showAnalysesPopup, setShowAnalysesPopup] = useState(false)

  // Filter analysisCollection to exclude analyses already on the sample when editing
  const availableAnalysisCollection = useMemo(() => {
    if (!isEdit || !initial.analysisName || initial.analysisName.length === 0) {
      return analysisCollection
    }
    
    // Get the initial analysis IDs to exclude
    const initialAnalysisIds = Array.isArray(initial.analysisName) 
      ? initial.analysisName 
      : [initial.analysisName]
    
    // Filter out analyses that are already on the sample
    return analysisCollection.filter(analysis => 
      !initialAnalysisIds.includes(analysis.id)
    )
  }, [isEdit, initial.analysisName, analysisCollection])

  // Build product collection from productAnalysis instead of sampleGroups
  const productCollectionFromAnalysis = useMemo(() => {
    const productNames = Object.keys(productAnalysis).filter((name) => name && name !== '')
    return productNames.map((name) => ({ id: name, name: name }))
  }, [productAnalysis])

  // Keep track of previous product selection so we only auto-fill on change
  const prevProductRef = useRef(form.productName)

  // Track if the current product is from the predefined list (not creatable) or user-created (creatable)
  const isProductFromList = useRef(false)

  // When `initial` or `isEdit` changes (popup opened for different item), reset form state
  useEffect(() => {
    setForm({
      sampleName: initial.sampleName || "",
      analysisName: isEdit ? [] : (initial.analysisName || []),
      observationId: initial.observationId || "1",
      numberOfObservations: initial.numberOfObservations || 1,
      arrival: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('yyyy-MM-dd') : DateTime.now().setZone(teamTimezone).startOf('day').toFormat('yyyy-MM-dd'),
      arrivalTime: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('HH:mm') : '00:00',
      deadline: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('yyyy-MM-dd') : "",
      deadlineTime: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('HH:mm') : '23:59',
      priority: initial.priority || 2,
      status: initial.status || 'TODO',
      productName: initial.productName || null,
      exclusive: !!initial.exclusive,
      isFlagged: !!initial.isFlagged,
    })
    prevProductRef.current = initial.productName || null
    // Check if initial product is from the predefined list
    isProductFromList.current = initial.productName ? productCollectionFromAnalysis.some(p => p.id === initial.productName) : false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, isEdit, teamTimezone])

  // Auto-select analyses when adding a sample and productName changes
  useEffect(() => {
    if (isEdit) return
    const product = form.productName || null
    // If no product is selected (null) or the 'None' option is selected (empty string), do not auto-load analyses
    if (product === '' || product === null) return
    const prev = prevProductRef.current || ''
    if (product === prev) return

    // Get analyses for the selected product from the productAnalysis object
    const analysesForProduct = productAnalysis[product]
    if (!analysesForProduct || !Array.isArray(analysesForProduct) || analysesForProduct.length === 0) {
      // Clear analyses when product has no analyses defined
      setForm((prev) => ({ ...prev, analysisName: [] }))
      prevProductRef.current = product
      return
    }

    // Map analysis names to canonical ids from analysisCollection
    const normalized = analysesForProduct.map((item) => {
      const analysisName = item.analysis_name
      const foundByName = analysisCollection.find((ac) => String(ac.name) === String(analysisName))
      if (foundByName) return foundByName.id
      // fallback: maybe the name is actually an id or other identifier
      const foundById = analysisCollection.find((ac) => String(ac.id) === String(analysisName))
      if (foundById) return foundById.id
      return null
    }).filter(Boolean)

    if (normalized.length > 0) {
      setForm((prev) => ({ ...prev, analysisName: normalized }))
    }
    prevProductRef.current = product
  }, [form.productName, productAnalysis, analysisCollection, isEdit])

  const [showMore, setShowMore] = useState(false)

  const submit = () => {
    // Build payload differently for edit vs add
    const analysisIds = (Array.isArray(form.analysisName) ? form.analysisName : [form.analysisName]).filter(Boolean)

    // Edit mode: only allow specific fields and ability to add analyses
    if (isEdit) {
      if (!form.sampleName || !form.deadline) return
      const deadlineISO = DateTime.fromISO(`${form.deadline}T${form.deadlineTime || '23:59'}`, { zone: teamTimezone }).toUTC().toISO()
      const payload = {
        sampleName: form.sampleName,
        priority: parseInt(form.priority, 10),
        deadline: deadlineISO,
        observationId: form.observationId,
        analysisIds: analysisIds,
      }
      onSubmit(payload)
      return
    }

    // Add mode: full create payload
    if (!form.sampleName || !analysisIds.length || !form.arrival || !form.deadline) return
    const arrivalISO = DateTime.fromISO(`${form.arrival}T${form.arrivalTime || '00:00'}`, { zone: teamTimezone }).toUTC().toISO()
    const deadlineISO = DateTime.fromISO(`${form.deadline}T${form.deadlineTime || '23:59'}`, { zone: teamTimezone }).toUTC().toISO()
    const payload = {
      sampleName: form.sampleName,
      analysisIds: analysisIds,
      observationId: form.observationId,
      numberOfObservations: parseInt(form.numberOfObservations, 10) || 1,
      arrival: arrivalISO,
      deadline: deadlineISO,
      priority: parseInt(form.priority, 10),
      status: form.status,
      productName: form.productName || null,
      exclusive: !!form.exclusive,
      isFlagged: !!form.isFlagged,
    }

    onSubmit(payload)
  }

  // Get analyses for the currently selected product
  const currentProductAnalyses = useMemo(() => {
    const productName = form.productName
    if (!productName || productName === '') return []

    const analysesForProduct = productAnalysis[productName]
    if (!analysesForProduct || !Array.isArray(analysesForProduct)) return []

    // Map to full analysis objects from analysisCollection and include the relationship ID
    const mapped = analysesForProduct
      .map((item) => {
        const analysisName = item.analysis_name
        const foundByName = analysisCollection.find((ac) => String(ac.name) === String(analysisName))
        if (foundByName) return { ...foundByName, relationshipId: item.id }
        const foundById = analysisCollection.find((ac) => String(ac.id) === String(analysisName))
        if (foundById) return { ...foundById, relationshipId: item.id }
        return null
      })
      .filter(Boolean)

    // Sort alphabetically by name
    return mapped.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [form.productName, productAnalysis, analysisCollection])

  const handleDeleteAnalysis = (relationshipId, analysisId) => {
    try {
      // Update prevProductRef to prevent auto-fill from re-triggering
      prevProductRef.current = form.productName

      dispatch(sampleView.removeProductAnalysis(relationshipId))

      // Also remove the analysis from the form's analysisName array if it's selected
      if (form.analysisName && form.analysisName.length > 0) {
        const updatedAnalysisName = form.analysisName.filter(id => id !== analysisId)
        setForm({ ...form, analysisName: updatedAnalysisName })
      }
    } catch (error) {
      console.error("Error removing product-analysis relationship:", error)
    }
  }

  return (
    <ConfigContainer title={title} submitHandler={submit} cancelHandler={onCancel}>
      <div className="add-sample-form">
        <div>
          <Container>
            <Title>{t("sampleName")}<div className="red-text">*</div></Title>
            <Text required value={form.sampleName} onChange={(v) => setForm({ ...form, sampleName: v })} />
          </Container>

          <Container>
            <Title>{t("productName")}<div className="red-text">*</div></Title>
            <Select
              collection={productCollectionFromAnalysis}
              value={form.productName || null}
              creatable={!isProductFromList.current}
              onChange={(items) => {
                const id = Array.isArray(items) ? items[0] : items
                // Track if the selected product is from the predefined list
                isProductFromList.current = id ? productCollectionFromAnalysis.some(p => p.id === id) : false
                // Clear analyses field when "None" is selected (id will be null or '')
                if (!id || id === '' || id === null) {
                  setForm({ ...form, productName: id, analysisName: [] })
                  // Reset prevProductRef so selecting the same product again will trigger auto-fill
                  prevProductRef.current = null
                } else {
                  setForm({ ...form, productName: id })
                }
              }}
              isMulti={false}
              closeMenuOnSelect={true}
              includeNone={true}
            />
          </Container>

          <Container>
            <Title>{isEdit ? t("addAnalyses") : t("analyses")}{!isEdit && <div className="red-text">*</div>}</Title>
            <div className="AddEditSamplePopup-analyses-container">
              <div className="AddEditSamplePopup-analyses-select">
                <Select
                  collection={availableAnalysisCollection}
                  value={form.analysisName}
                  onChange={(items) => {
                    if (!items || items.length === 0) {
                      setForm({ ...form, analysisName: [] })
                    } else {
                      const ids = Array.isArray(items) ? items : [items]
                      const normalized = ids.map((it) => (it && it.id !== undefined ? it.id : it))
                      setForm({ ...form, analysisName: normalized })
                    }
                  }}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  includeNone={false}
                />
              </div>
              {!isEdit && (
                <Button
                  variant="primary"
                  onClick={() => setShowAnalysesPopup(true)}
                  disabled={!form.productName || form.productName === ''}
                  title={t("viewAllAnalysesForProduct")}
                >
                  <FiSettings />
                </Button>
              )}
            </div>
          </Container>

          <Container>
            <Title>{t("deadline")}<div className="red-text">*</div></Title>
            <div className="AddEditSamplePopup-datetime-container">
              <Text type="date" required value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
              <Text type="time" required value={form.deadlineTime} onChange={(v) => setForm({ ...form, deadlineTime: v })} />
            </div>
          </Container>

          {/* Show Priority field always when editing */}
          {isEdit && (
            <Container>
              <Title>{t("priority")}<div className="red-text">*</div></Title>
              <Select
                collection={[
                  { id: 1, name: `1 ${t("priorityHigh")}` },
                  { id: 2, name: `2 ${t("priorityMedium")}` },
                  { id: 3, name: `3 ${t("priorityLow")}` }
                ]}
                value={form.priority}
                onChange={(value) => setForm({ ...form, priority: value })}
                isMulti={false}
                closeMenuOnSelect={true}
                includeNone={false}
              />
            </Container>
          )}
          {/* When editing we only show minimal editable fields; otherwise show the extra fields under Show more */}
          {!isEdit && (
            <>
              <div className="AddEditSamplePopup-show-more-container">
                <div />
                <Button variant="link" onClick={() => setShowMore(!showMore)}>{showMore ? t("hideMore") : t("showMore")}</Button>
              </div>

              {showMore && (
                <>
                  <Container>
                    <Title>{t("arrival")}<div className="red-text">*</div></Title>
                    <div className="AddEditSamplePopup-datetime-container">
                      <Text type="date" required value={form.arrival} onChange={(v) => setForm({ ...form, arrival: v })} />
                      <Text type="time" required value={form.arrivalTime} onChange={(v) => setForm({ ...form, arrivalTime: v })} />
                    </div>
                  </Container>

                  <Container>
                    <Title>{t("priority")}<div className="red-text">*</div></Title>
                    <Select
                      collection={[
                        { id: 1, name: `1 ${t("priorityHigh")}` },
                        { id: 2, name: `2 ${t("priorityMedium")}` },
                        { id: 3, name: `3 ${t("priorityLow")}` }
                      ]}
                      value={form.priority}
                      onChange={(value) => setForm({ ...form, priority: value })}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      includeNone={false}
                    />
                  </Container>

                  <Container>
                    <Title>{t("sampleId")}</Title>
                    <Text value={form.observationId} onChange={(v) => setForm({ ...form, observationId: v })} />
                  </Container>

                  <Container>
                    <Title>{t("numberOfObservations")}</Title>
                    <Text value={form.numberOfObservations} onChange={(v) => setForm({ ...form, numberOfObservations: parseInt(v, 10) || 0 })} />
                  </Container>

                  <Container>
                    <Title>{t("exclusive")}</Title>
                    <Checkbox
                      id="sample-exclusive"
                      checked={!!form.exclusive}
                      onChange={(checked) => setForm({ ...form, exclusive: checked })}
                    />
                  </Container>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Analyses Popup */}
      {showAnalysesPopup && (
        <Popup
          title={t("analysesRelatedTo", { product: form.productName || t("selectedProduct") })}
          onCancel={() => setShowAnalysesPopup(false)}
          className="AddEditSamplePopup-analyses-popup"
        >
          <div className="AddEditSamplePopup-analyses-list">
            {currentProductAnalyses.length > 0 ? (
              <div className="AddEditSamplePopup-analyses-items">
                {currentProductAnalyses.map((analysis, index) => (
                  <div key={analysis.id || index} className="AddEditSamplePopup-analysis-item">
                    <div className="AddEditSamplePopup-analysis-content">
                      <span className="AddEditSamplePopup-analysis-name">{analysis.name}</span>
                      {analysis.description && (
                        <span className="AddEditSamplePopup-analysis-description">{analysis.description}</span>
                      )}
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      className="AddEditSamplePopup-delete-button"
                      onClick={() => handleDeleteAnalysis(analysis.relationshipId, analysis.id)}
                      title={t("removeAnalysisFromProduct", { name: analysis.name })}
                    >
                      <FiTrash2 />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="AddEditSamplePopup-no-analyses">
                {t("noAnalysesForProduct")}
              </div>
            )}
          </div>
        </Popup>
      )}
    </ConfigContainer>
  )
}
